/**
 * 61. Reorder Planner — min/max suggestions; planned purchase orders.
 *
 * Arabic and English (C7): every word from the catalogs; the item, warehouse
 * and quantities in each explanation are placeholders the locale writes,
 * never typed into the sentence.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  Badge,
  BlockedActionExplanation,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatQuantity } from "@/lib/money";

interface ReorderRow {
  id: string;
  productSku: string;
  productName: string;
  warehouseName: string;
  onHand: string;
  minimumLevel: string;
  maximumLevel: string;
  suggestedQuantity: string;
  plannedPoExists: boolean;
}

export function ReorderPlannerPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<ReorderRow>(
    ["inventory", "replenishment"],
    "/inventory/replenishment/recommendations",
  );

  const needsAction = (data?.items ?? []).filter((r) => !r.plannedPoExists);

  return (
    <div>
      <PageHeader
        title={t("inventory.reorder.title")}
        subtitle={t("inventory.reorder.subtitle")}
        actions={
          <PendingButton variant="primary" reason="afterLaunch" slice="F23">
            {t("inventory.reorder.generate")}
          </PendingButton>
        }
      />
      {needsAction.length > 0 && (
        <BlockedActionExplanation
          title={t("inventory.reorder.belowTitle")}
          reasons={needsAction.map((r) => ({
            code: "STOCK_BELOW_MINIMUM",
            message: t("inventory.reorder.below", {
              sku: r.productSku,
              warehouse: r.warehouseName,
              onHand: formatQuantity(r.onHand),
              min: formatQuantity(r.minimumLevel),
            }),
            resolutionPath: t("inventory.reorder.belowFix"),
          }))}
        />
      )}
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.reorder.list"), data.total)}>
          <DataTable<ReorderRow>
            rows={data.items}
            columns={[
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "productName", label: t("fields.product") },
              { key: "warehouseName", label: t("fields.warehouse") },
              {
                key: "onHand",
                label: t("inventory.reorder.onHand"),
                numeric: true,
                render: (r) => formatQuantity(r.onHand),
              },
              {
                key: "minimumLevel",
                label: t("inventory.reorder.min"),
                numeric: true,
                render: (r) => formatQuantity(r.minimumLevel),
              },
              {
                key: "maximumLevel",
                label: t("inventory.reorder.max"),
                numeric: true,
                render: (r) => formatQuantity(r.maximumLevel),
              },
              {
                key: "suggestedQuantity",
                label: t("inventory.reorder.suggested"),
                numeric: true,
                render: (r) => (
                  <strong>{formatQuantity(r.suggestedQuantity)}</strong>
                ),
              },
              {
                key: "plannedPoExists",
                label: t("inventory.reorder.plannedPo"),
                render: (r) => (
                  <Badge tone={r.plannedPoExists ? "success" : "warning"}>
                    {r.plannedPoExists
                      ? t("inventory.reorder.exists")
                      : t("inventory.reorder.missing")}
                  </Badge>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
